import React from "react";
import { ArrowRight } from "lucide-react";

type Cta = { label: string; onClick: () => void };

type LaunchHeroProps = {
  image: string;
  imageAlt: string;
  /** Pills above the title, e.g. "SPACIA MOVE" · "Coming Soon" · launch date */
  pills: React.ReactNode;
  titleLines: string[];
  lead: string;
  body: string;
  /** Text opacity differs per page: Move uses solid white, Eat fades lead and body */
  leadClassName?: string;
  bodyClassName?: string;
  /** Strength of the left-to-right shade over the photo */
  sideShade?: number;
  /** Desktop padding under the buttons, from the design */
  desktopBottomPadding?: number;
  /** Border colour class for the outlined button: solid white on Move, white/30 on Eat */
  secondaryBorderClassName?: string;
  primaryCta: Cta;
  secondaryCta: Cta;
};

/** Full-height hero for the "coming soon" category pages (Move, Eat). */
export function LaunchHero({
  image,
  imageAlt,
  pills,
  titleLines,
  lead,
  body,
  leadClassName = "text-white",
  bodyClassName = "text-white",
  sideShade = 0.5,
  desktopBottomPadding = 80,
  secondaryBorderClassName = "border-white/30",
  primaryCta,
  secondaryCta,
}: LaunchHeroProps) {
  return (
    <section className="relative flex min-h-[640px] items-end overflow-hidden lg:h-[903px]">
      <img src={image} alt={imageAlt} className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,10,0.9)] via-[rgba(10,10,10,0.4)] to-[rgba(10,10,10,0.1)]" />
      <div
        className="absolute inset-0"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(10,10,10,${sideShade}) 0%, rgba(10,10,10,0) 100%)` }}
      />

      <div
        className="relative mx-auto w-full max-w-[1440px] px-[16px] pb-24 pt-32 sm:px-8 lg:pb-[var(--hero-pb)]"
        style={{ "--hero-pb": `${desktopBottomPadding}px` } as React.CSSProperties}
      >
        <div className="max-w-[672px]">
          <div className="flex flex-wrap items-center gap-[12px]">{pills}</div>
          <h1 className="pt-6 text-[44px] font-bold leading-[1.05] tracking-[-1.8px] text-white sm:text-[72px] sm:leading-[73.44px]">
            {titleLines.map((line, index) => (
              <React.Fragment key={line}>
                {index > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </h1>
          <p className={`max-w-[576px] pt-6 text-[18px] leading-[29.25px] ${leadClassName}`}>{lead}</p>
          <p className={`max-w-[576px] pt-[20px] text-[16px] leading-[26px] ${bodyClassName}`}>{body}</p>
          <div className="flex flex-wrap gap-[16px] pt-8">
            <button
              type="button"
              onClick={primaryCta.onClick}
              className="inline-flex items-center gap-2 rounded-full bg-[#017bef] px-7 py-3.5 text-[14px] font-bold leading-5 text-white drop-shadow-[0_4px_8px_rgba(1,123,239,0.3)] transition-colors hover:bg-[#0062c0]"
            >
              {primaryCta.label}
              <ArrowRight className="size-4" />
            </button>
            <button
              type="button"
              onClick={secondaryCta.onClick}
              className={`rounded-full border-[1px] border-solid px-7 py-3.5 text-[14px] font-semibold leading-5 text-white transition-colors hover:bg-white/10 ${secondaryBorderClassName}`}
            >
              {secondaryCta.label}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

type ComingSoonBandProps = {
  title: string;
  description: string;
  launch: string;
  primaryCta: Cta;
  secondaryCta: Cta;
};

/** Dark navy "X Is Coming to SPACIA" band. */
export function ComingSoonBand({ title, description, launch, primaryCta, secondaryCta }: ComingSoonBandProps) {
  return (
    <section className="relative overflow-hidden bg-[#012d6e] py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[500px] -translate-x-1/2 rounded-full bg-[#017bef]/20 blur-[64px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-[250px] w-[400px] -translate-x-1/2 rounded-full bg-[#2563eb]/[0.15] blur-[64px]" />

      <div className="relative mx-auto flex max-w-[768px] flex-col items-center px-[16px] text-center sm:px-8">
        <span className="rounded-full bg-[#017bef] px-[16px] py-1.5 text-[12px] font-bold uppercase leading-4 tracking-[1.2px] text-white">
          Coming Soon
        </span>
        <h2 className="pt-[20px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-white sm:text-[48px] sm:leading-[48px]">
          {title}
        </h2>
        <p className="max-w-[576px] pb-[16px] pt-[20px] text-[18px] leading-[29.25px] text-white/60">{description}</p>
        <span className="rounded-full border-[1px] border-solid border-white/20 px-[20px] py-2.5 text-[14px] font-semibold leading-5 text-white/60">
          {launch}
        </span>
        <div className="flex flex-wrap justify-center gap-[16px] pt-10">
          <button
            type="button"
            onClick={primaryCta.onClick}
            className="inline-flex items-center gap-2 rounded-full bg-[#017bef] px-8 py-[16px] text-[16px] font-bold leading-6 text-white drop-shadow-[0_4px_12px_rgba(1,123,239,0.3)] transition-colors hover:bg-[#0062c0]"
          >
            {primaryCta.label}
            <ArrowRight className="size-[18px]" />
          </button>
          <button
            type="button"
            onClick={secondaryCta.onClick}
            className="rounded-full border-[1px] border-solid border-white/20 px-8 py-[16px] text-[16px] font-semibold leading-6 text-white transition-colors hover:bg-white/10"
          >
            {secondaryCta.label}
          </button>
        </div>
      </div>
    </section>
  );
}

export type PlatformCard = {
  label: string;
  color: string;
  title: string;
  description: string;
  /** Outlined in blue to mark the current page's pillar (Eat) */
  highlight?: boolean;
};

type PlatformCardsProps = {
  eyebrow: string;
  title: string;
  cards: PlatformCard[];
  footnote: string;
};

/** Four pillar cards with a short footnote, on a tinted background. */
export function PlatformCards({ eyebrow, title, cards, footnote }: PlatformCardsProps) {
  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center px-[16px] text-center sm:px-8">
        <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">{eyebrow}</p>
        <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          {title}
        </h2>

        <div className="grid w-full max-w-[768px] grid-cols-2 gap-[20px] pt-14 sm:grid-cols-4">
          {cards.map(card => (
            <div
              key={card.label}
              className={`flex flex-col items-center rounded-2xl border-[1px] border-solid bg-white p-[20px] ${
                card.highlight
                  ? "border-[#017bef]/30 drop-shadow-[0_4px_10px_rgba(1,123,239,0.08)]"
                  : "border-[#e5e7eb]"
              }`}
            >
              <span
                className="flex size-12 items-center justify-center rounded-[14px] text-[10px] font-bold leading-[15px] tracking-[1px] text-white shadow-sm"
                style={{ backgroundColor: card.color }}
              >
                {card.label}
              </span>
              <p className="pt-[12px] text-[14px] font-bold leading-5 text-[#0a0a0a]">{card.title}</p>
              <p className="pt-0.5 text-[12px] leading-4 text-[#717182]">{card.description}</p>
            </div>
          ))}
        </div>

        <p className="max-w-[576px] pt-10 text-[16px] leading-6 text-[#717182]">{footnote}</p>
      </div>
    </section>
  );
}

/** Coloured pillar pills shown in the closing card. */
export function PillarPills({ pills }: { pills: { label: string; color: string }[] }) {
  return (
    <div className="flex flex-wrap justify-center gap-[12px] pt-8">
      {pills.map(pill => (
        <span
          key={pill.label}
          className="rounded-full px-[20px] py-2 text-[14px] font-bold leading-5 text-white"
          style={{ backgroundColor: pill.color }}
        >
          {pill.label}
        </span>
      ))}
    </div>
  );
}
